import {
  chakra,
  forwardRef,
  type TableRowProps as ChakraTableRowProps,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import { useTableStyles } from '../TableStylesContext';
import { useTableContext } from '../utils/context';
import { TrProvider } from '../utils/rowContext';

const RELAXED_PADDING_BY_SIZE: Record<string, number> = {
  sm: 0.75,
  md: 1,
};

type CssPropAssertedType = Record<string, unknown> & {
  ':hover:not(:has([role="group"]:hover))'?: Record<string, unknown>;
  ':active:not(:has([role="group"]:hover))'?: Record<string, unknown>;
};

export interface TrProps extends ChakraTableRowProps {
  isInteractive?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  index?: number;
}

export const Tr = forwardRef<TrProps, 'tr'>(
  (
    {
      isInteractive,
      isDisabled,
      isSelected,
      children,
      __css: cssProp,
      index,
      zebraIndex,
      ...restProps
    },
    ref,
  ) => {
    const { tr: trStyles } = useTableStyles() ?? {};

    const { zebra, density, size } = useTableContext() ?? {};

    const __css = useMemo(() => {
      // TODO: migrate cell style logic to Td component via new TrContext
      const innerCss: Record<string, unknown> = { ...trStyles, ...cssProp };

      if (zebra != null && !isSelected && index == null) {
        const zebraProps = {
          backgroundColor: 'page.plateLight',
          '--cell-background': 'var(--chakra-colors-page-plateLight)',
          ...((cssProp as CssPropAssertedType)?.[
            `:nth-of-type(${zebra}) > td > *`
          ] ?? {}),
        };
        if (typeof zebraIndex === 'number') {
          const isEven = zebraIndex % 2 === 0;
          if (zebra === 'even' && isEven) {
            innerCss[`td > *`] = zebraProps;
          } else if (zebra === 'odd' && !isEven) {
            innerCss[`td > *`] = zebraProps;
          }
        } else {
          innerCss[`:nth-of-type(${zebra}) > td > *`] = zebraProps;
        }
      }

      if (isSelected) {
        innerCss['> td > *'] = {
          ...(isDisabled ? { opacity: 0.4 } : null),
          backgroundColor: 'interaction.surfaceActive',
          '--cell-background': 'var(--chakra-colors-interaction-surfaceActive)',
          color: 'feature.primary',
          ...((cssProp as CssPropAssertedType)?.['> td > *'] ?? {}),
        };

        innerCss['> th'] = {
          backgroundColor: 'interaction.active',
          '--cell-background': 'var(--chakra-colors-interaction-active)',
          color: 'feature.onInteraction',
          ...((cssProp as CssPropAssertedType)?.['> th'] ?? {}),
        };
      }

      if (isInteractive) {
        innerCss.cursor = 'pointer';

        if (!isSelected) {
          innerCss[':hover:not(:has([role="group"]:hover))'] = {
            ...((cssProp as CssPropAssertedType)?.[
              ':hover:not(:has([role="group"]:hover))'
            ] ?? {}),
            '> td > *': {
              backgroundColor: 'interaction.surfaceHover',
              '--cell-background':
                'var(--chakra-colors-interaction-surfaceHover)',
              color: 'feature.primary',
              ...((cssProp as CssPropAssertedType)?.[
                ':hover:not(:has([role="group"]:hover))'
              ]?.['> td > *'] ?? {}),
            },
          };

          innerCss[':active:not(:has([role="group"]:hover))'] = {
            ...((cssProp as CssPropAssertedType)?.[
              ':active:not(:has([role="group"]:hover))'
            ] ?? {}),
            '> td > *': {
              backgroundColor: 'interaction.surfaceActive',
              '--cell-background':
                'var(--chakra-colors-interaction-surfaceActive)',
              ...((cssProp as CssPropAssertedType)?.[
                ':active:not(:has([role="group"]:hover))'
              ]?.['> td > *'] ?? {}),
            },
          };

          innerCss['> td > *'] = {
            ...(isDisabled ? { opacity: 0.4 } : null),
            color: 'feature.secondary',
            ...((cssProp as CssPropAssertedType)?.['> td > *'] ?? {}),
          };
        }

        innerCss[':focus-visible'] = {
          boxShadow: 'focusInset',
          outline: 'none',
          ...((cssProp as CssPropAssertedType)?.[':focus-visible'] ?? {}),
        };
      }

      if (density === 'relaxed') {
        innerCss._notLast = {
          td: {
            borderBottomWidth: 0,
          },
        };
        innerCss._first = {
          td: {
            pt: RELAXED_PADDING_BY_SIZE[size],
          },
        };
        innerCss._last = {
          td: {
            pb: RELAXED_PADDING_BY_SIZE[size],
          },
        };
      }
      return innerCss;
    }, [
      trStyles,
      cssProp,
      size,
      density,
      zebra,
      index,
      isSelected,
      isInteractive,
      isDisabled,
      zebraIndex,
    ]);

    const trContextValue = useMemo(
      () => ({ index, isInteractive }),
      [index, isInteractive],
    );

    return (
      <chakra.tr
        ref={ref}
        __css={__css}
        tabIndex={isInteractive ? 0 : undefined}
        {...restProps}
      >
        <TrProvider value={trContextValue}>{children}</TrProvider>
      </chakra.tr>
    );
  },
);
Tr.displayName = 'Tr';
